/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useSonnerToast } from '@/shared/ui';
import type { ClientOption } from '@/features/visits/hooks/useAgencyClients';
import { SaleService } from '../../services/sale-service';
import { useSaleSettings } from '../../hooks/useSaleSettings';
import { buildSaleFormSchema, type SaleFormData } from './schema';

/**
 * Reads the refusal the backend actually sent, same reasoning as the sale
 * detail actions: a fixed string would discard the only part of the
 * response that tells the broker what is wrong.
 */
function refusalReason(error: unknown): string | undefined {
  const body = (error as { body?: { error?: string } } | undefined)?.body;
  return body?.error;
}

export interface UseCreateSaleFormProps {
  agencyId: string;
  clients: ClientOption[];
  onSuccess?: () => void;
}

/**
 * Builds the create-sale request body, sending only the optional fields
 * the broker actually set.
 *
 * `commission_percentage` uses `!== undefined` rather than truthiness: an
 * explicit 0 is a sale with no commission and must be sent, while an empty
 * field asks the backend for the agency's default.
 *
 * @param data - The validated form values.
 * @returns The request body for `SaleService.createSale`.
 */
function buildCreateSaleBody(data: SaleFormData) {
  return {
    property_id: data.propertyId,
    buyer_client_id: data.buyerId,
    accepted_value: data.acceptedValue,
    ...(data.commissionPercentage !== undefined
      ? { commission_percentage: data.commissionPercentage }
      : {}),
    ...(data.responsibleBrokerEmail
      ? { responsible_broker_email: data.responsibleBrokerEmail }
      : {}),
  };
}

/** The seed condition and mutations the commission-default effect needs. */
interface CommissionSeedDeps {
  defaultCommissionPercentage: number | null;
  form: ReturnType<typeof useForm<SaleFormData>>;
}

/**
 * Seeds the commission field with the agency default while it is still
 * untouched, so the same number is not retyped on every sale. A broker who
 * typed something -- zero included -- keeps it.
 *
 * @param deps - The default value and the form controller.
 */
function seedCommissionDefault({ defaultCommissionPercentage, form }: CommissionSeedDeps): void {
  if (defaultCommissionPercentage === null) return;
  if (form.getFieldState('commissionPercentage').isDirty) return;
  if (form.getValues('commissionPercentage') !== undefined) return;
  form.setValue('commissionPercentage', defaultCommissionPercentage);
}

/**
 * Form controller for the create-sale drawer.
 *
 * The buyer is picked from the agency's existing clients, but the record
 * itself only ever stores a name/document snapshot — the same shape the
 * deal-won path builds. The owner is not asked here at all: the property
 * already names its registered owner, and the backend reads it from
 * there, same as the deal-won path does.
 */
export function useCreateSaleForm({ agencyId, clients, onSuccess }: UseCreateSaleFormProps) {
  const { t } = useTranslation('features/sales');
  const { showSuccess } = useSonnerToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  const schema = useMemo(() => buildSaleFormSchema(t), [t]);
  const form = useForm<SaleFormData>({
    resolver: zodResolver(schema),
    defaultValues: { propertyId: '', buyerId: '' },
    mode: 'onSubmit',
  });

  const { defaultCommissionPercentage } = useSaleSettings(agencyId);
  useEffect(() => {
    seedCommissionDefault({ defaultCommissionPercentage, form });
  }, [defaultCommissionPercentage, form]);

  const clientById = useMemo(() => new Map(clients.map((c) => [c.id, c])), [clients]);

  const onValid = useCallback(async (data: SaleFormData) => {
    // Still resolved against the loaded options, but only to confirm the
    // pick is a real client of this agency -- the id itself is what gets
    // sent. This used to build a {name, document} snapshot here and throw
    // the id away, which is how a sale ended up unable to point back at
    // the client whose name it was showing.
    if (!clientById.has(data.buyerId)) {
      toast.error(t('create.errors.buyerUnresolved'));
      return;
    }

    setIsSubmitting(true);
    setHasValidationErrors(false);

    try {
      await SaleService.createSale(agencyId, buildCreateSaleBody(data));
      showSuccess(t('create.success'));
      form.reset();
      onSuccess?.();
    } catch (error) {
      toast.error(refusalReason(error) ?? t('create.errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  }, [agencyId, clientById, form, onSuccess, showSuccess, t]);

  const onInvalid = useCallback(() => setHasValidationErrors(true), []);

  const handleSubmit = useCallback(
    () => form.handleSubmit(onValid, onInvalid)(),
    [form, onValid, onInvalid],
  );

  return {
    form,
    isSubmitting,
    hasValidationErrors,
    handleSubmit,
  };
}
